import { BitkitLink, BitkitTooltip } from '@bitrise/bitkit-v2';

import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import GitProvider from '@/models/GitProvider';

type PullRequestOrCommitHashLinkProps = {
  gitProvider?: GitProvider;
  commitHash?: string;
  commitHashUrl?: string;
  pullRequestId?: number;
  pullRequestUrl?: string;
  size?: '2' | '3';
};

function getViewOnProviderText(id: GitProvider | undefined, type: 'pullRequest' | 'commitHash'): string {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return type === 'pullRequest' ? 'View pull request on GitHub' : 'View commit on GitHub';
    case GitProvider.GITLAB:
      return type === 'pullRequest' ? 'View pull request on GitLab' : 'View commit on GitLab';
    case GitProvider.BITBUCKET:
      return type === 'pullRequest' ? 'View pull request on Bitbucket' : 'View commit on Bitbucket';
    default:
      return '';
  }
}

const PullRequest = (props: Omit<PullRequestOrCommitHashLinkProps, 'commitHash' | 'commitHashUrl'>) => {
  const { gitProvider, pullRequestId, pullRequestUrl, size } = props;
  const viewOnProviderText = getViewOnProviderText(gitProvider, 'pullRequest');

  if (pullRequestId && pullRequestId > 0) {
    return (
      <BitkitTooltip disabled={!pullRequestUrl || !viewOnProviderText} text={viewOnProviderText}>
        <BuildMetaData
          textProps={{ whiteSpace: 'nowrap', textStyle: size === '3' ? 'body/lg/regular' : 'body/md/regular' }}
          flexShrink={0}
        >
          {pullRequestUrl ? (
            <BitkitLink href={pullRequestUrl} isExternal whiteSpace="nowrap" colorVariant="purple">
              #{pullRequestId}
            </BitkitLink>
          ) : (
            `#${pullRequestId}`
          )}
        </BuildMetaData>
      </BitkitTooltip>
    );
  }

  return null;
};

const CommitHash = (
  props: Omit<PullRequestOrCommitHashLinkProps, 'pullRequestId' | 'pullRequestUrl'> & { withParenthesis?: boolean },
) => {
  const { gitProvider, commitHash, commitHashUrl, size, withParenthesis } = props;

  if (!commitHash) {
    return null;
  }
  const viewOnProviderText = getViewOnProviderText(gitProvider, 'commitHash');
  const slicedCommitHash = commitHash.slice(0, 7);
  const text = withParenthesis ? `(${slicedCommitHash})` : slicedCommitHash;

  return (
    <BitkitTooltip disabled={!commitHashUrl || !viewOnProviderText} text={viewOnProviderText}>
      <BuildMetaData
        textProps={{ whiteSpace: 'nowrap', textStyle: size === '3' ? 'body/lg/regular' : 'body/md/regular' }}
        flexShrink={0}
      >
        {commitHashUrl ? (
          <BitkitLink href={commitHashUrl} isExternal whiteSpace="nowrap" colorVariant="purple">
            {text}
          </BitkitLink>
        ) : (
          `${text}`
        )}
      </BuildMetaData>
    </BitkitTooltip>
  );
};

const PullRequestOrCommitHashLink = ({
  gitProvider,
  commitHash,
  commitHashUrl,
  pullRequestId,
  pullRequestUrl,
  size = '2',
}: PullRequestOrCommitHashLinkProps) => {
  return (
    <>
      <PullRequest {...{ gitProvider, pullRequestId, pullRequestUrl, size }} />
      <CommitHash
        {...{ gitProvider, commitHash, commitHashUrl, size }}
        withParenthesis={!!pullRequestId && pullRequestId > 0}
      />
    </>
  );
};

export default PullRequestOrCommitHashLink;
